import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from '../icons';
import { Button } from '../ui/button';

interface CarouselProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  ariaLabel: string;
}

export function Carousel<T>({
  items,
  renderItem,
  ariaLabel,
}: CarouselProps<T>) {
  const trackRef = useRef<HTMLUListElement | null>(null);
  const [index, setIndex] = useState(0);
  const hasMultipleItems = items.length > 1;

  const scrollTo = useCallback((targetIndex: number) => {
    const track = trackRef.current;
    if (!track) return;

    const slide = track.children[targetIndex] as HTMLElement | undefined;
    if (slide) {
      setIndex(targetIndex);
      slide.scrollIntoView({
        behavior: 'smooth',
        inline: 'start',
        block: 'nearest',
      });
    }
  }, []);

  const prev = () => scrollTo(Math.max(0, index - 1));
  const next = () => scrollTo(Math.min(items.length - 1, index + 1));

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        // Includes the 16px track gap item spacing safely
        const pitch = track.clientWidth + 16;
        const computedIndex = Math.round(track.scrollLeft / pitch);

        setIndex(Math.max(0, Math.min(items.length - 1, computedIndex)));
        ticking = false;
      });
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    return () => track.removeEventListener('scroll', onScroll);
  }, [items.length]);

  if (items.length === 0) return null;

  return (
    <section
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      className="tw:relative tw:w-full tw:mb-10"
    >
      <div className="tw:relative tw:flex tw:items-center tw:justify-center tw:w-full">
        {/* Left Arrow Button */}
        {hasMultipleItems && (
          <Button
            type="button"
            size="icon"
            aria-label="Previous slide"
            onClick={prev}
            disabled={index === 0}
            className="tw:absolute tw:hidden tw:md:block tw:left-0 tw:top-1/2 tw:-translate-y-1/2 tw:z-10 tw:h-10 tw:w-10 tw:lg:h-12 tw:lg:w-12 tw:min-w-[32px] tw:items-center tw:justify-center tw:bg-gray-900 tw:text-white tw:disabled:opacity-40 tw:disabled:tw:cursor-not-allowed tw:disabled:hover:tw:bg-[#111827] tw:disabled:hover:tw:text-white tw:transition-all tw:duration-250 tw:ease-[cubic-bezier(0.22,0.61,0.36,1)] tw:hover:tw:bg-white tw:hover:tw:text-primary tw:rounded-none"
          >
            <ArrowLeftIcon />
          </Button>
        )}

        {/* Scrolling Track */}
        <ul
          ref={trackRef}
          role="list"
          aria-live="polite"
          className="tw:grid tw:auto-cols-[100%] tw:grid-flow-col tw:overflow-x-auto tw:gap-4 tw:scroll-smooth tw:snap-x tw:snap-mandatory focus-visible:tw:outline-none tw:px-0 tw:[scrollbar-width:none] tw:[-ms-overflow-style:none] tw:[&::-webkit-scrollbar]:tw:hidden"
        >
          {items.map((item, i) => (
            <li
              key={i}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${items.length}`}
              className={`tw:list-none tw:snap-start tw:snap-always tw:flex tw:justify-center tw:w-full ${hasMultipleItems ? 'tw:md:px-12 tw:lg:px-16' : ''}`}
            >
              <div className="tw:w-full tw:flex tw:justify-center">
                {renderItem(item, i)}
              </div>
            </li>
          ))}
        </ul>

        {/* Right Arrow Button */}
        {hasMultipleItems && (
          <Button
            type="button"
            size="icon"
            aria-label="Next slide"
            onClick={next}
            disabled={index === items.length - 1}
            className="tw:absolute tw:hidden tw:md:block tw:right-0 tw:top-1/2 tw:-translate-y-1/2 tw:z-10 tw:h-10 tw:w-10 tw:lg:h-12 tw:lg:w-12 tw:min-w-[32px] tw:items-center tw:justify-center tw:bg-gray-900 tw:text-white tw:disabled:opacity-40 tw:disabled:tw:cursor-not-allowed tw:disabled:hover:tw:bg-[#111827] tw:disabled:hover:tw:text-white tw:transition-all tw:duration-250 tw:ease-[cubic-bezier(0.22,0.61,0.36,1)] tw:hover:tw:bg-white tw:hover:tw:text-primary tw:rounded-none"
          >
            <ArrowRightIcon />
          </Button>
        )}
      </div>

      {/* Navigation Indicators (Dots) */}
      {hasMultipleItems && (
        <div
          className="tw:mt-2 tw:flex tw:justify-center tw:gap-2"
          role="tablist"
          aria-label="Slides"
        >
          {items.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === index}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => scrollTo(i)}
              className={`tw:z-10 tw:h-[15px] tw:w-[15px] tw:rounded-full tw:border tw:border-[#111827] tw:transition-colors tw:duration-200 ${
                i === index ? 'tw:bg-[#111827]' : 'tw:bg-white'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
